import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexto/AuthContexto';
import { Toaster } from 'sonner';
import Layout from '@/componentes/Layout';
import Login from '@/paginas/Login';
import Landing from '@/paginas/Landing';
import Registro from '@/paginas/Registro';
import Gracias from '@/paginas/Gracias';
import Privacidad from '@/paginas/Privacidad';
import Terminos from '@/paginas/Terminos';
import Dashboard from '@/paginas/Dashboard';
import Clientes from '@/paginas/Clientes';
import ClienteDetalle from '@/paginas/ClienteDetalle';
import Vehiculos from '@/paginas/Vehiculos';
import VehiculoDetalle from '@/paginas/VehiculoDetalle';
import Ordenes from '@/paginas/Ordenes';
import OrdenDetalle from '@/paginas/OrdenDetalle';
import OrdenFormulario from '@/paginas/OrdenFormulario';
import Pagos from '@/paginas/Pagos';
import Configuracion from '@/paginas/Configuracion';
import AdminPanel from '@/paginas/AdminPanel';
import OrdenImprimir from '@/paginas/OrdenImprimir';
import ReciboImprimir from '@/paginas/ReciboImprimir';
import SuscripcionBloqueada from '@/paginas/SuscripcionBloqueada';
import PruebaExpirada from '@/paginas/PruebaExpirada';
import Suscripcion from '@/paginas/Suscripcion';
import SuscripcionExito from '@/paginas/SuscripcionExito';
import Citas from '@/paginas/Citas';
import Recordatorios from '@/paginas/Recordatorios';
import Staff from '@/paginas/Staff';
import RecuperarPassword from '@/paginas/RecuperarPassword';
import RestablecerPassword from '@/paginas/RestablecerPassword';
import VerificarEmail from '@/paginas/VerificarEmail';
import VerificacionPendiente from '@/paginas/VerificacionPendiente';
import Inventario from '@/paginas/Inventario';
import Contabilidad from '@/paginas/Contabilidad';
import CorteCaja from '@/paginas/CorteCaja';
import CarwashDashboard from '@/paginas/CarwashDashboard';
import CarwashTurnos from '@/paginas/CarwashTurnos';
import CarwashServicios from '@/paginas/CarwashServicios';
import CarwashCaja from '@/paginas/CarwashCaja';
import CarwashMostrador from '@/paginas/CarwashMostrador';
import CarwashContabilidad from '@/paginas/CarwashContabilidad';
import CarwashNomina from '@/paginas/CarwashNomina';
import CarwashCitas from '@/paginas/CarwashCitas';
import CarwashOperarios from '@/paginas/CarwashOperarios';
import CarwashStaff from '@/paginas/CarwashStaff';
import CarwashOperario from '@/paginas/CarwashOperario';
import CarwashAgendaConfig from '@/paginas/CarwashAgendaConfig';
import CarwashCierres from '@/paginas/CarwashCierres';
import CarwashClientes from '@/paginas/CarwashClientes';
import TallerCierres from '@/paginas/TallerCierres';
import AgendaPublica from '@/paginas/AgendaPublica';
import Novedades from '@/paginas/Novedades';
import Planes from '@/paginas/Planes';
import Corporativo from '@/paginas/Corporativo';
import TicketTermico from '@/paginas/TicketTermico';
import Complementos from '@/paginas/Complementos';
import FeatureGate from '@/componentes/FeatureGate';
import TiendaGate from '@/componentes/TiendaGate';
import '@/App.css';

function estaSuscripcionBloqueada(suscripcion, rol) {
  if (rol === 'super_admin') return false;
  if (!suscripcion) return false;
  if (suscripcion.estado === 'suspendido') return 'suspendido';
  if (suscripcion.estado === 'expirado') return 'expirado';
  // estado === 'en_gracia' → NO bloquear (dar tiempo a que el pago se procese)
  if (suscripcion.estado === 'en_gracia') return false;
  if (suscripcion.vence) {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const vence = new Date(suscripcion.vence);
    // Ventana de gracia de 5 dias post-vencimiento antes de bloquear.
    // Cubre el retraso del webhook de MP/Stripe (24-72h tipico).
    const graciaMs = 5 * 24 * 60 * 60 * 1000;
    if (vence.getTime() + graciaMs < hoy.getTime()) return 'expirado';
  }
  return false;
}

function RutaProtegida({ children, roles }) {
  const { usuario, suscripcion, cargando } = useAuth();
  const location = useLocation();
  if (cargando) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!usuario) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(usuario.rol)) {
    if (usuario.rol === 'super_admin') return <Navigate to="/admin" replace />;
    if (usuario.rol === 'operario') return <Navigate to="/operario" replace />;
    if (usuario.rol === 'cajero') return <Navigate to="/carwash/turnos" replace />;
    if (usuario.rol === 'mecanico') return <Navigate to="/ordenes" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  // Operario: skip subscription check and layout
  if (usuario.rol === 'operario') return children;
  // Mecanico and Administrativo: also use Layout + subscription check
  const bloqueado = estaSuscripcionBloqueada(suscripcion, usuario.rol);
  // Whitelist: usuarios expirados/suspendidos SI pueden acceder a /suscripcion,
  // /planes y /suscripcion/exito para poder pagar, cancelar o actualizar tarjeta.
  // El resto de rutas siguen bloqueadas.
  const RUTAS_BILLING_PERMITIDAS = ['/suscripcion', '/planes', '/suscripcion/exito'];
  const esRutaBilling = RUTAS_BILLING_PERMITIDAS.some(
    (r) => location.pathname === r || location.pathname.startsWith(`${r}/`)
  );
  if (bloqueado && esRutaBilling) return <Layout>{children}</Layout>;
  if (bloqueado === 'expirado') return <PruebaExpirada />;
  if (bloqueado === 'suspendido') return <SuscripcionBloqueada />;
  return <Layout>{children}</Layout>;
}

function RutaImpresion({ children }) {
  const { usuario, cargando } = useAuth();
  if (cargando) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!usuario) return <Navigate to="/login" replace />;
  return children;
}

function RutaPublica({ children }) {
  const { usuario, cargando } = useAuth();
  if (cargando) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (usuario) {
    if (usuario.rol === 'super_admin') return <Navigate to="/admin" replace />;
    if (usuario.rol === 'operario') return <Navigate to="/operario" replace />;
    if (usuario.rol === 'cajero') return <Navigate to="/carwash/turnos" replace />;
    if (usuario.rol === 'mecanico') return <Navigate to="/ordenes" replace />;
    if (usuario.tipo_negocio === 'carwash') return <Navigate to="/carwash" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RutaPublica><Landing /></RutaPublica>} />
      <Route path="/login" element={<RutaPublica><Login /></RutaPublica>} />
      <Route path="/registro" element={<RutaPublica><Registro /></RutaPublica>} />
      <Route path="/recuperar-password" element={<RecuperarPassword />} />
      <Route path="/restablecer-password" element={<RestablecerPassword />} />
      <Route path="/verificar-email" element={<VerificarEmail />} />
      <Route path="/verificacion-pendiente" element={<VerificacionPendiente />} />
      <Route path="/gracias" element={<Gracias />} />
      <Route path="/planes" element={<Planes />} />
      <Route path="/privacidad" element={<Privacidad />} />
      <Route path="/terminos" element={<Terminos />} />

      <Route path="/dashboard" element={<RutaProtegida roles={['propietario', 'administrativo', 'mecanico']}><Dashboard /></RutaProtegida>} />
      <Route path="/clientes" element={<RutaProtegida roles={['propietario', 'administrativo']}><Clientes /></RutaProtegida>} />
      <Route path="/clientes/:id" element={<RutaProtegida roles={['propietario', 'administrativo']}><ClienteDetalle /></RutaProtegida>} />
      <Route path="/vehiculos" element={<RutaProtegida roles={['propietario', 'administrativo']}><Vehiculos /></RutaProtegida>} />
      <Route path="/vehiculos/:id" element={<RutaProtegida roles={['propietario', 'administrativo']}><VehiculoDetalle /></RutaProtegida>} />
      <Route path="/ordenes" element={<RutaProtegida roles={['propietario', 'administrativo', 'mecanico']}><Ordenes /></RutaProtegida>} />
      <Route path="/ordenes/nueva" element={<RutaProtegida roles={['propietario', 'administrativo']}><OrdenFormulario /></RutaProtegida>} />
      <Route path="/ordenes/:id" element={<RutaProtegida roles={['propietario', 'administrativo', 'mecanico']}><OrdenDetalle /></RutaProtegida>} />
      <Route path="/ordenes/:id/editar" element={<RutaProtegida roles={['propietario', 'administrativo']}><OrdenFormulario /></RutaProtegida>} />
      <Route path="/ordenes/:id/imprimir" element={<RutaImpresion><OrdenImprimir /></RutaImpresion>} />
      <Route path="/ordenes/:id/recibo/:pagoId" element={<RutaImpresion><ReciboImprimir /></RutaImpresion>} />
      <Route path="/pagos" element={<RutaProtegida roles={['propietario', 'administrativo']}><Pagos /></RutaProtegida>} />
      <Route path="/citas" element={<RutaProtegida roles={['propietario', 'administrativo']}><FeatureGate feature="agenda_publica"><Citas /></FeatureGate></RutaProtegida>} />
      <Route path="/recordatorios" element={<RutaProtegida roles={['propietario', 'administrativo']}><Recordatorios /></RutaProtegida>} />
      <Route path="/staff" element={<RutaProtegida roles={['propietario', 'administrativo']}><FeatureGate feature="staff_multiple"><Staff /></FeatureGate></RutaProtegida>} />
      <Route path="/contabilidad" element={<RutaProtegida roles={['propietario', 'administrativo']}><Contabilidad /></RutaProtegida>} />
      <Route path="/cierres" element={<RutaProtegida roles={['propietario', 'administrativo']}><FeatureGate feature="cierres_contables"><TallerCierres /></FeatureGate></RutaProtegida>} />
      <Route path="/inventario" element={<RutaProtegida roles={['propietario', 'administrativo']}><FeatureGate feature="inventario"><Inventario /></FeatureGate></RutaProtegida>} />
      <Route path="/corte-caja" element={<RutaProtegida roles={['propietario', 'administrativo']}><CorteCaja /></RutaProtegida>} />
      <Route path="/configuracion" element={<RutaProtegida roles={['propietario']}><Configuracion /></RutaProtegida>} />
      <Route path="/suscripcion" element={<RutaProtegida roles={['propietario']}><Suscripcion /></RutaProtegida>} />
      <Route path="/complementos" element={<RutaProtegida roles={['propietario']}><TiendaGate><Complementos /></TiendaGate></RutaProtegida>} />
      <Route path="/corporativo" element={<RutaProtegida roles={['propietario']}><FeatureGate feature="multi_sucursal"><Corporativo /></FeatureGate></RutaProtegida>} />
      <Route path="/novedades" element={<RutaProtegida roles={['propietario', 'administrativo', 'mecanico']}><Novedades /></RutaProtegida>} />
      <Route path="/suscripcion/exito" element={<SuscripcionExito />} />
      <Route path="/admin" element={<RutaProtegida roles={['super_admin']}><AdminPanel /></RutaProtegida>} />

      {/* Car Wash routes */}
      <Route path="/carwash" element={<RutaProtegida roles={['propietario', 'administrativo']}><CarwashDashboard /></RutaProtegida>} />
      <Route path="/carwash/turnos" element={<RutaProtegida roles={['propietario', 'administrativo', 'cajero']}><CarwashTurnos /></RutaProtegida>} />
      <Route path="/carwash/servicios" element={<RutaProtegida roles={['propietario', 'administrativo']}><CarwashServicios /></RutaProtegida>} />
      <Route path="/carwash/caja" element={<RutaProtegida roles={['propietario', 'administrativo', 'cajero']}><CarwashCaja /></RutaProtegida>} />
      <Route path="/carwash/mostrador" element={<RutaProtegida roles={['propietario', 'administrativo', 'cajero']}><CarwashMostrador /></RutaProtegida>} />
      <Route path="/carwash/contabilidad" element={<RutaProtegida roles={['propietario', 'administrativo']}><CarwashContabilidad /></RutaProtegida>} />
      <Route path="/carwash/nomina" element={<RutaProtegida roles={['propietario', 'administrativo']}><FeatureGate feature="nomina"><CarwashNomina /></FeatureGate></RutaProtegida>} />
      <Route path="/carwash/citas" element={<RutaProtegida roles={['propietario', 'administrativo', 'cajero']}><FeatureGate feature="agenda_publica"><CarwashCitas /></FeatureGate></RutaProtegida>} />
      <Route path="/carwash/staff" element={<RutaProtegida roles={['propietario', 'administrativo']}><FeatureGate feature="staff_multiple"><CarwashStaff /></FeatureGate></RutaProtegida>} />
      <Route path="/carwash/operarios" element={<RutaProtegida roles={['propietario', 'administrativo']}><CarwashOperarios /></RutaProtegida>} />
      <Route path="/carwash/agenda" element={<RutaProtegida roles={['propietario', 'administrativo']}><FeatureGate feature="agenda_publica"><CarwashAgendaConfig /></FeatureGate></RutaProtegida>} />
      <Route path="/carwash/cierres" element={<RutaProtegida roles={['propietario', 'administrativo']}><FeatureGate feature="cierres_contables"><CarwashCierres /></FeatureGate></RutaProtegida>} />
      <Route path="/carwash/clientes" element={<RutaProtegida roles={['propietario', 'administrativo', 'cajero']}><CarwashClientes /></RutaProtegida>} />
      <Route path="/carwash/inventario" element={<RutaProtegida roles={['propietario', 'administrativo', 'cajero']}><FeatureGate feature="inventario"><Inventario /></FeatureGate></RutaProtegida>} />
      <Route path="/carwash/turnos/:id/ticket-termico" element={<RutaImpresion><TicketTermico /></RutaImpresion>} />

      {/* Operario route (no layout) */}
      <Route path="/operario" element={<RutaProtegida roles={['operario']}><CarwashOperario /></RutaProtegida>} />

      {/* Public booking (no auth) */}
      <Route path="/agenda/:slug" element={<AgendaPublica />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster
          theme="dark"
          position="top-right"
          richColors
          toastOptions={{
            style: {
              background: '#1e293b',
              border: '1px solid #334155',
              color: '#f8fafc',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
